import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './Calculator.css';

function Calculator() {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [memory, setMemory] = useState(0);
  const [scientificMode, setScientificMode] = useState(false);

  const inputNumber = (num) => {
    if (waitingForOperand) {
      setDisplay(String(num));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(num) : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const clearEntry = () => {
    setDisplay('0');
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
      
      // Add to history
      const historyEntry = `${currentValue} ${operation} ${inputValue} = ${newValue}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]); // Keep last 10 calculations
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : 0;
      case '%':
        return firstValue % secondValue;
      case '^':
        return Math.pow(firstValue, secondValue);
      default:
        return secondValue;
    }
  };

  const performEquals = () => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      
      const historyEntry = `${previousValue} ${operation} ${inputValue} = ${newValue}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
      
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const performScientificOperation = (func) => {
    const inputValue = parseFloat(display);
    let result;

    switch (func) {
      case 'sin':
        result = Math.sin(inputValue * Math.PI / 180);
        break;
      case 'cos':
        result = Math.cos(inputValue * Math.PI / 180);
        break;
      case 'tan':
        result = Math.tan(inputValue * Math.PI / 180);
        break;
      case 'log':
        result = Math.log10(inputValue);
        break;
      case 'ln':
        result = Math.log(inputValue);
        break;
      case 'sqrt':
        result = Math.sqrt(inputValue);
        break;
      case 'square':
        result = inputValue * inputValue;
        break;
      case 'factorial':
        result = factorial(inputValue);
        break;
      case '1/x':
        result = 1 / inputValue;
        break;
      case 'pi':
        result = Math.PI;
        break;
      case 'e':
        result = Math.E;
        break;
      default:
        result = inputValue;
    }

    const historyEntry = `${func}(${inputValue}) = ${result}`;
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    
    setDisplay(String(result));
    setWaitingForOperand(true);
  };

  const factorial = (n) => {
    if (n < 0 || n !== Math.floor(n)) return NaN;
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  const handleMemory = (action) => {
    const inputValue = parseFloat(display);
    
    switch (action) {
      case 'MC':
        setMemory(0);
        break;
      case 'MR':
        setDisplay(String(memory));
        setWaitingForOperand(true);
        break;
      case 'M+':
        setMemory(memory + inputValue);
        break;
      case 'M-':
        setMemory(memory - inputValue);
        break;
      case 'MS':
        setMemory(inputValue);
        break;
      default:
        break;
    }
  };

  const handleKeyPress = useCallback((event) => {
    const { key } = event;
    
    if (key >= '0' && key <= '9') {
      inputNumber(parseInt(key));
    } else if (key === '.') {
      inputDecimal();
    } else if (key === '+' || key === '-') {
      performOperation(key);
    } else if (key === '*') {
      performOperation('×');
    } else if (key === '/') {
      event.preventDefault();
      performOperation('÷');
    } else if (key === 'Enter' || key === '=') {
      performEquals();
    } else if (key === 'Escape') {
      clear();
    } else if (key === 'Backspace') {
      if (display.length > 1) {
        setDisplay(display.slice(0, -1));
      } else {
        setDisplay('0');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleKeyPress intentionally depends on latest handlers; listing them would require wrapping each in useCallback
  }, [display, operation, previousValue, waitingForOperand]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Online Calculator - Free Scientific Calculator",
    "description": "Free online calculator with scientific functions, memory operations, and calculation history. Supports basic and advanced mathematical operations.",
    "url": "https://infi-tools.com/calculator",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Basic arithmetic operations",
      "Scientific functions (sin, cos, tan, log)",
      "Memory operations (M+, M-, MS, MR, MC)",
      "Calculation history",
      "Keyboard support",
      "Standard and scientific modes"
    ]
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="Free Online Calculator - Scientific Calculator with History"
        description="Free online calculator with scientific functions, memory operations, and calculation history. Supports trigonometry, logarithms, and advanced mathematical operations. No download required."
        keywords="online calculator, free calculator, scientific calculator, math calculator, calculator with history, memory calculator, trigonometry calculator, logarithm calculator, web calculator"
        canonicalUrl="https://infi-tools.com/calculator"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="calc-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Calculator</h1>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="6977263813" style={{ marginBottom: '20px' }} />

        <div className="calculator-container">
          <div className="calculator">
            {/* Display */}
            <div className="calc-display">
              <div className="display-value">{display}</div>
              {memory !== 0 && <div className="memory-indicator">M</div>}
            </div>

            {/* Mode Toggle */}
            <div className="calc-modes">
              <button 
                className={`mode-btn ${!scientificMode ? 'active' : ''}`}
                onClick={() => setScientificMode(false)}
              >
                Standard
              </button>
              <button 
                className={`mode-btn ${scientificMode ? 'active' : ''}`}
                onClick={() => setScientificMode(true)}
              >
                Scientific
              </button>
              <button 
                className="history-btn"
                onClick={() => setShowHistory(!showHistory)}
              >
                📜 History
              </button>
            </div>

            <div className="calc-body">
              {/* Memory Functions */}
              <div className="memory-row">
                <button className="btn memory" onClick={() => handleMemory('MC')}>MC</button>
                <button className="btn memory" onClick={() => handleMemory('MR')}>MR</button>
                <button className="btn memory" onClick={() => handleMemory('M+')}>M+</button>
                <button className="btn memory" onClick={() => handleMemory('M-')}>M-</button>
                <button className="btn memory" onClick={() => handleMemory('MS')}>MS</button>
              </div>

              {/* Scientific Functions (if enabled) */}
              {scientificMode && (
                <div className="scientific-functions">
                  <button className="btn scientific" onClick={() => performScientificOperation('sin')}>sin</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('cos')}>cos</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('tan')}>tan</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('log')}>log</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('ln')}>ln</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('sqrt')}>√</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('square')}>x²</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('factorial')}>x!</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('1/x')}>1/x</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('pi')}>π</button>
                  <button className="btn scientific" onClick={() => performScientificOperation('e')}>e</button>
                  <button className="btn operator" onClick={() => performOperation('^')}>x^y</button>
                </div>
              )}

              {/* Main Calculator Buttons */}
              <div className="calc-buttons">
                <button className="btn clear" onClick={clear}>C</button>
                <button className="btn clear" onClick={clearEntry}>CE</button>
                <button className="btn operator" onClick={() => performOperation('%')}>%</button>
                <button className="btn operator" onClick={() => performOperation('÷')}>÷</button>

                <button className="btn number" onClick={() => inputNumber(7)}>7</button>
                <button className="btn number" onClick={() => inputNumber(8)}>8</button>
                <button className="btn number" onClick={() => inputNumber(9)}>9</button>
                <button className="btn operator" onClick={() => performOperation('×')}>×</button>

                <button className="btn number" onClick={() => inputNumber(4)}>4</button>
                <button className="btn number" onClick={() => inputNumber(5)}>5</button>
                <button className="btn number" onClick={() => inputNumber(6)}>6</button>
                <button className="btn operator" onClick={() => performOperation('-')}>-</button>

                <button className="btn number" onClick={() => inputNumber(1)}>1</button>
                <button className="btn number" onClick={() => inputNumber(2)}>2</button>
                <button className="btn number" onClick={() => inputNumber(3)}>3</button>
                <button className="btn operator" onClick={() => performOperation('+')}>+</button>

                <button className="btn number zero" onClick={() => inputNumber(0)}>0</button>
                <button className="btn number" onClick={inputDecimal}>.</button>
                <button className="btn equals" onClick={performEquals}>=</button>
              </div>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="history-panel">
              <h3>Calculation History</h3>
              {history.length === 0 ? (
                <p className="no-history">No calculations yet</p>
              ) : (
                <div className="history-list">
                  {history.map((entry, index) => (
                    <div key={index} className="history-item">
                      {entry}
                    </div>
                  ))}
                </div>
              )}
              <button 
                className="clear-history"
                onClick={() => setHistory([])}
              >
                Clear History
              </button>
            </div>
          )}
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="7308769443" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default Calculator; 